import { useAppStore } from '@/state/store';
import { formatLength } from '@/lib/utils/units';

function Card({
  label,
  value,
  unit,
  note,
}: {
  label: string;
  value: string;
  unit?: string;
  note?: string;
}) {
  return (
    <div className="info-card">
      <div className="info-card-label">{label}</div>
      <div className="info-card-value">
        {value}
        {unit && <span className="info-card-unit">{unit}</span>}
      </div>
      {note && <div className="info-card-note">{note}</div>}
    </div>
  );
}

export function ModelInfoPanel() {
  const model = useAppStore((s) => s.model);
  const displayUnit = useAppStore((s) => s.viewer.displayUnit);

  if (!model) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">No model loaded</div>
        <div className="empty-state-subtitle">Import a .C file to see full model measurements here.</div>
      </div>
    );
  }

  const m = model.metrics;
  const volumeCm3 = m.volume / 1000;
  const weightOz = m.estimatedWeightGrams / 28.3495;

  return (
    <div className="panel-scroll">
      <h2 className="section-title">{m.modelName}</h2>
      <p className="section-subtitle">Derived from {m.totalProfilePoints} trimmed profile points.</p>

      <div className="info-grid">
        <Card label="Length" value={formatLength(m.length, displayUnit, 2).split(' ')[0]} unit={displayUnit} />
        <Card
          label="Barrel Diameter"
          value={formatLength(m.barrelDiameter, displayUnit, 3).split(' ')[0]}
          unit={displayUnit}
        />
        <Card
          label="Handle Diameter"
          value={formatLength(m.handleDiameter, displayUnit, 3).split(' ')[0]}
          unit={displayUnit}
        />
        <Card
          label="Knob Diameter"
          value={formatLength(m.knobDiameter, displayUnit, 3).split(' ')[0]}
          unit={displayUnit}
        />
        <Card
          label="Sweet Spot Position"
          value={formatLength(m.sweetSpotPosition, displayUnit, 2).split(' ')[0]}
          unit={displayUnit}
          note="Estimated ~150mm from the barrel tip"
        />
        <Card
          label="Balance Point"
          value={formatLength(m.balancePoint, displayUnit, 2).split(' ')[0]}
          unit={displayUnit}
          note="Estimated from geometric volume, assumes uniform wood density"
        />
        <Card label="Total Profile Points" value={String(m.totalProfilePoints)} />
        <Card label="Detected Units" value={model.normalized.unit === 'mm' ? 'Millimeters' : 'Inches'} />
        <Card
          label="Bounding Box"
          value={`${formatLength(m.boundingBox.x, displayUnit, 1)} × ${formatLength(
            m.boundingBox.y,
            displayUnit,
            1,
          )} × ${formatLength(m.boundingBox.z, displayUnit, 1)}`}
        />
        <Card label="Volume" value={volumeCm3.toFixed(1)} unit="cm³" />
        <Card
          label="Estimated Weight"
          value={m.estimatedWeightGrams.toFixed(0)}
          unit={`g (${weightOz.toFixed(1)} oz)`}
          note="Assumes hard maple density (0.68 g/cm³) — verify against scale"
        />
      </div>
    </div>
  );
}
